/**
 * Pure ranking math for the live "pick amount → see rank → pay" projection.
 *
 * Mirrors the backend's actual board rule (repository.getBoard): the board is
 * the set of PAID listings sorted by amount DESC. Given a candidate amount and
 * the current board, the projected rank is:
 *
 *   rank = 1 + (number of paid listings whose amount is strictly GREATER than
 *          the candidate amount)
 *
 * i.e. a candidate ties-or-beats any listing it is >=, so it slots ABOVE the
 * highest listing it doesn't strictly beat. This matches firstbid.lol's loop
 * (e.g. $1 → last place, $38 → #1) and is honest: it uses the same ordering the
 * real board uses.
 */

function listingDollars(listing) {
  if (!listing) return 0;
  if (typeof listing.amount_cents === "number" && Number.isFinite(listing.amount_cents)) {
    return listing.amount_cents / 100;
  }
  return Number(listing.amount) || 0;
}

/**
 * @param {number} amount - candidate bid amount in dollars
 * @param {Array<{amount:number}>} board - current paid listings (any order)
 * @returns {number} 1-based projected rank
 */
export function rankForAmount(amount, board = []) {
  const amt = Number(amount) || 0;
  const above = board.filter((l) => listingDollars(l) >= amt).length;
  return above + 1;
}

/**
 * The smallest amount that would take rank #1 given the current board.
 * Returns null if the candidate already holds #1 (or the board is empty).
 * @param {Array<{amount:number}>} board
 * @param {number} currentAmount - the user's current selected/entered amount
 */
export function overtakeAmount(board = [], currentAmount = 0) {
  const top = board.reduce((m, l) => Math.max(m, listingDollars(l)), 0);
  if (currentAmount > top) return null;
  return top + 1;
}

/**
 * The smallest $ that projects to a given rank (1-based).
 * Used by Take #N buttons to prefill the overlay.
 * rank 1 -> top board amount + 1 (or $1 if empty)
 * rank N -> amount of the listing currently at rank N-1, + 1
 * If the target rank is an empty ghost slot -> $1
 */
export function takeAmountForRank(board = [], rank = 1) {
  if (rank < 1) return 1;
  const sorted = [...board].sort((a, b) => listingDollars(b) - listingDollars(a));
  // rank 1: beat the top listing
  if (rank === 1) {
    const top = sorted[0] ? listingDollars(sorted[0]) : 0;
    return top + 1;
  }
  // rank N: must beat the listing currently AT rank N (first-listed wins ties)
  const target = sorted[rank - 1]; // 0-indexed
  if (!target) return 1;
  return listingDollars(target) + 1;
}
